import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Trophy, Layers, BarChart3, LayoutGrid, Activity } from "lucide-react";
import { startCheckout } from "@/lib/billing";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BENEFITS = [
  { icon: Trophy, title: "All Top 5 picks", desc: "Every daily Top 5 play, not just one." },
  { icon: Activity, title: "Full pick breakdowns", desc: "AI reasoning, score signals, and risk factors." },
  { icon: LayoutGrid, title: "All Picks board", desc: "Every published pick across all sports." },
  { icon: Layers, title: "Parlay of the Day", desc: "Daily curated parlay with all legs unlocked." },
  { icon: BarChart3, title: "Full history & stats", desc: "Complete record, units, and pick archive." },
];

export default function UpgradeModal({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        {/* Gradient header */}
        <div className="relative px-6 pt-7 pb-5 text-center bg-gradient-card border-b border-border/60">
          <div className="absolute inset-0 bg-gradient-primary opacity-10 pointer-events-none" />
          <div className="relative">
            <div className="mx-auto mb-3 grid place-items-center w-14 h-14 rounded-2xl bg-gradient-accent text-accent-foreground shadow-glow">
              <Sparkles className="w-7 h-7" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl md:text-3xl">Unlock <span className="te-grad-text">Pro</span></DialogTitle>
              <DialogDescription>
                Every edge — full picks, parlays, and the complete board.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="px-6 py-5">
          <ul className="space-y-2.5">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex gap-3 items-start rounded-xl border border-border/60 bg-secondary/40 p-3 hover:border-primary/40 transition-colors">
                <div className="grid place-items-center w-9 h-9 rounded-lg bg-primary/15 text-primary shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-success" /> {title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-5 flex flex-col gap-2">
            <Button className="w-full bg-gradient-accent text-accent-foreground hover:opacity-90 shadow-glow" onClick={async () => { try { await startCheckout(); } catch (e: any) { toast.error(e?.message ?? "Unable to start checkout"); } }}>
                <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Pro
              </Button>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Maybe later</Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center mt-3">
            Stripe billing launches soon · cancel anytime
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
